/**
 * Payment settings domain types.
 *
 * Enum string values match the events API verbatim (GET/PATCH
 * `/events/api/v1/settings/payment/`) so the mapper only renames/groups fields.
 * The six dropdown option lists are served by the API in the response `options`
 * block (`settings.options.*`) — the FE no longer hardcodes them. Note the three
 * reminder fields use `never` as the off/manual sentinel — NOT `manual_only`
 * (which the backend rejects with a 400).
 */

import type { SelectOption } from "@/Shared/CSelect/CSelect";

export type EstimatedInvoiceTrigger =
  | "when_booking_confirmed" // default
  | "when_proposal_sent"
  | "manual_only";

export type FinalInvoiceTrigger =
  | "when_booking_completed" // default
  | "on_event_day"
  | "manual_only";

export type InvoiceDelivery =
  | "email_and_portal" // default
  | "email_only"
  | "portal_only";

export type DepositReminder =
  | "on_signing_day"
  | "1_day_after_signed"
  | "3_days_after_signed" // default
  | "7_days_after_signed"
  | "never";

export type BalanceReminder =
  | "3_days_before_due"
  | "7_days_before_due" // default
  | "14_days_before_due"
  | "30_days_before_due"
  | "never";

export type OverdueReminderFrequency =
  | "daily_until_paid"
  | "every_3_days_until_paid" // default
  | "weekly_until_paid"
  | "never";

export interface InvoiceGenerationSettings {
  estimatedInvoiceCreated: EstimatedInvoiceTrigger;
  finalInvoiceCreated: FinalInvoiceTrigger;
  invoiceNumberingFormat: string;
  invoiceStartingNumber: number;
  invoiceDelivery: InvoiceDelivery;
  showItemisedLineItems: boolean;
  allowPdfDownload: boolean;
  setInvoicePassword: boolean;
  invoicePassword: string;
}

export interface PaymentReminderSettings {
  depositReminder: DepositReminder;
  balanceReminder: BalanceReminder;
  overdueReminderFrequency: OverdueReminderFrequency;
  sendDepositReminderAuto: boolean;
  sendBalanceDueReminderAuto: boolean;
}

/** API-served dropdown option lists for this screen (read-only). */
export interface PaymentOptions {
  estimatedInvoice: SelectOption[];
  finalInvoice: SelectOption[];
  invoiceDelivery: SelectOption[];
  depositReminder: SelectOption[];
  balanceReminder: SelectOption[];
  overdueReminderFrequency: SelectOption[];
}

export interface PaymentSettings {
  invoice: InvoiceGenerationSettings;
  reminders: PaymentReminderSettings;
  /** Dropdown choices from the API `options` block. */
  options: PaymentOptions;
}

/** Design-seed defaults (the values shown selected in the Figma). */
export function defaultPaymentSettings(): PaymentSettings {
  return {
    invoice: {
      estimatedInvoiceCreated: "when_booking_confirmed",
      finalInvoiceCreated: "when_booking_completed",
      invoiceNumberingFormat: "INV-{YEAR}-{SEQ}",
      invoiceStartingNumber: 1001,
      invoiceDelivery: "email_and_portal",
      showItemisedLineItems: false,
      allowPdfDownload: true,
      setInvoicePassword: true,
      invoicePassword: "",
    },
    reminders: {
      depositReminder: "3_days_after_signed",
      balanceReminder: "7_days_before_due",
      overdueReminderFrequency: "every_3_days_until_paid",
      sendDepositReminderAuto: true,
      sendBalanceDueReminderAuto: false,
    },
    // Options come from the API; empty on the read-failure fallback path.
    options: {
      estimatedInvoice: [],
      finalInvoice: [],
      invoiceDelivery: [],
      depositReminder: [],
      balanceReminder: [],
      overdueReminderFrequency: [],
    },
  };
}
